import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  LocalFireDepartment as FireIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { stickerTrackingService, UserStickerStats, PlanStickerStats } from '../services/stickerTrackingService';

interface StickerStatsProps {
  userId: string;
  open: boolean;
  onClose: () => void;
  userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
}

const StickerStats: React.FC<StickerStatsProps> = ({ userId, open, onClose, userLanguage }) => {
  const [userStats, setUserStats] = useState<UserStickerStats | null>(null);
  const [planStats, setPlanStats] = useState<PlanStickerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | false>('overview');

  // Debug logging
  console.log('📊 StickerStats component rendered with props:', { userId, open, onClose });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Language detection
  const isChinese = userLanguage === 'zh-CN';
  const isSpanish = userLanguage === 'es';
  const isFrench = userLanguage === 'fr';

  // Multi-language text mappings
  const getText = (key: string) => {
    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        stickerStatistics: '贴纸统计',
        overview: '概览',
        achievements: '成就',
        recentActivity: '最近活动',
        totalStickers: '总贴纸数',
        plansCompleted: '完成计划',
        daysCompleted: '完成天数',
        currentStreak: '当前连续',
        longestStreak: '最长连续',
        averageStickersPerPlan: '每计划平均贴纸',
        lastActivity: '最后活动',
        noDataAvailable: '暂无数据',
        loading: '加载中...',
        close: '关闭',
        stickersByPlanType: '按计划类型分类的贴纸',
        dynamic: '动态',
        template: '模板',
        communication: '沟通',
        recentStickerEvents: '最近贴纸事件',
        completedDay: '完成第{day}天',
        completedPlan: '完成计划',
        bonusStickers: '奖励贴纸',
        noRecentActivity: '暂无最近活动',
        viewDetails: '查看详情',
        total: '总计',
        completed: '已完成',
        inProgress: '进行中',
        notStarted: '未开始'
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        stickerStatistics: 'Estadísticas de Calcomanías',
        overview: 'Resumen',
        achievements: 'Logros',
        recentActivity: 'Actividad Reciente',
        totalStickers: 'Total de Calcomanías',
        plansCompleted: 'Planes Completados',
        daysCompleted: 'Días Completados',
        currentStreak: 'Racha Actual',
        longestStreak: 'Racha Más Larga',
        averageStickersPerPlan: 'Promedio de Calcomanías por Plan',
        lastActivity: 'Última Actividad',
        noDataAvailable: 'No hay datos disponibles',
        loading: 'Cargando...',
        close: 'Cerrar',
        stickersByPlanType: 'Calcomanías por Tipo de Plan',
        dynamic: 'Dinámico',
        template: 'Plantilla',
        communication: 'Comunicación',
        recentStickerEvents: 'Eventos Recientes de Calcomanías',
        completedDay: 'Día {day} Completado',
        completedPlan: 'Plan Completado',
        bonusStickers: 'Calcomanías de Bonificación',
        noRecentActivity: 'No hay actividad reciente',
        viewDetails: 'Ver Detalles',
        total: 'Total',
        completed: 'Completado',
        inProgress: 'En Progreso',
        notStarted: 'No Iniciado'
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        stickerStatistics: 'Statistiques d\'Autocollants',
        overview: 'Aperçu',
        achievements: 'Réalisations',
        recentActivity: 'Activité Récente',
        totalStickers: 'Total d\'Autocollants',
        plansCompleted: 'Plans Terminés',
        daysCompleted: 'Jours Terminés',
        currentStreak: 'Série Actuelle',
        longestStreak: 'Série la Plus Longue',
        averageStickersPerPlan: 'Moyenne d\'Autocollants par Plan',
        lastActivity: 'Dernière Activité',
        noDataAvailable: 'Aucune donnée disponible',
        loading: 'Chargement...',
        close: 'Fermer',
        stickersByPlanType: 'Autocollants par Type de Plan',
        dynamic: 'Dynamique',
        template: 'Modèle',
        communication: 'Communication',
        recentStickerEvents: 'Événements Récents d\'Autocollants',
        completedDay: 'Jour {day} Terminé',
        completedPlan: 'Plan Terminé',
        bonusStickers: 'Autocollants Bonus',
        noRecentActivity: 'Aucune activité récente',
        viewDetails: 'Voir les Détails',
        total: 'Total',
        completed: 'Terminé',
        inProgress: 'En Cours',
        notStarted: 'Non Commencé'
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        stickerStatistics: 'Sticker Statistics',
        overview: 'Overview',
        achievements: 'Achievements',
        recentActivity: 'Recent Activity',
        totalStickers: 'Total Stickers',
        plansCompleted: 'Plans Completed',
        daysCompleted: 'Days Completed',
        currentStreak: 'Current Streak',
        longestStreak: 'Longest Streak',
        averageStickersPerPlan: 'Average Stickers per Plan',
        lastActivity: 'Last Activity',
        noDataAvailable: 'No data available',
        loading: 'Loading...',
        close: 'Close',
        stickersByPlanType: 'Stickers by Plan Type',
        dynamic: 'Dynamic',
        template: 'Template',
        communication: 'Communication',
        recentStickerEvents: 'Recent Sticker Events',
        completedDay: 'Completed Day {day}',
        completedPlan: 'Completed Plan',
        bonusStickers: 'Bonus Stickers',
        noRecentActivity: 'No recent activity',
        viewDetails: 'View Details',
        total: 'Total',
        completed: 'Completed',
        inProgress: 'In Progress',
        notStarted: 'Not Started'
      };
      return englishTexts[key] || key;
    }
  };

  // Function to get translated achievement text
  const getTranslatedAchievementText = (achievementId: string, userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'): { name: string; description: string } => {
    const achievementTexts: Record<string, Record<string, { name: string; description: string }>> = {
      'first_sticker': {
        'zh-CN': { name: '第一步', description: '获得了您的第一张贴纸！' },
        'es': { name: 'Primeros Pasos', description: '¡Ganaste tu primera calcomanía!' },
        'fr': { name: 'Premiers Pas', description: 'Vous avez gagné votre premier autocollant !' },
        'en': { name: 'First Steps', description: 'Earned your first sticker!' }
      },
      'sticker_collector_10': {
        'zh-CN': { name: '贴纸收集者', description: '获得了10张贴纸！' },
        'es': { name: 'Coleccionista', description: '¡Ganaste 10 calcomanías!' },
        'fr': { name: 'Collectionneur', description: 'Vous avez gagné 10 autocollants !' },
        'en': { name: 'Sticker Collector', description: 'Earned 10 stickers!' }
      },
      'sticker_master_50': {
        'zh-CN': { name: '贴纸大师', description: '获得了50张贴纸！' },
        'es': { name: 'Maestro de Calcomanías', description: '¡Ganaste 50 calcomanías!' },
        'fr': { name: 'Maître des Autocollants', description: 'Vous avez gagné 50 autocollants !' },
        'en': { name: 'Sticker Master', description: 'Earned 50 stickers!' }
      },
      'sticker_legend_100': {
        'zh-CN': { name: '贴纸传奇', description: '获得了100张贴纸！' },
        'es': { name: 'Leyenda de Calcomanías', description: '¡Ganaste 100 calcomanías!' },
        'fr': { name: 'Légende des Autocollants', description: 'Vous avez gagné 100 autocollants !' },
        'en': { name: 'Sticker Legend', description: 'Earned 100 stickers!' }
      },
      'first_plan': {
        'zh-CN': { name: '计划完成者', description: '完成了您的第一个计划！' },
        'es': { name: 'Finalizador de Planes', description: '¡Completaste tu primer plan!' },
        'fr': { name: 'Finisseur de Plans', description: 'Vous avez terminé votre premier plan !' },
        'en': { name: 'Plan Finisher', description: 'Completed your first plan!' }
      },
      'plan_expert_5': {
        'zh-CN': { name: '计划专家', description: '完成了5个计划！' },
        'es': { name: 'Experto en Planes', description: '¡Completaste 5 planes!' },
        'fr': { name: 'Expert en Plans', description: 'Vous avez terminé 5 plans !' },
        'en': { name: 'Plan Expert', description: 'Completed 5 plans!' }
      },
      'streak_master_7': {
        'zh-CN': { name: '连续大师', description: '连续7天完成计划！' },
        'es': { name: 'Maestro de Racha', description: '¡Completaste planes 7 días seguidos!' },
        'fr': { name: 'Maître de Série', description: 'Vous avez terminé des plans 7 jours de suite !' },
        'en': { name: 'Streak Master', description: 'Completed plans for 7 days straight!' }
      },
      'streak_legend_30': {
        'zh-CN': { name: '连续传奇', description: '连续30天完成计划！' },
        'es': { name: 'Leyenda de Racha', description: '¡Completaste planes 30 días seguidos!' },
        'fr': { name: 'Légende de Série', description: 'Vous avez terminé des plans 30 jours de suite !' },
        'en': { name: 'Streak Legend', description: 'Completed plans for 30 days straight!' }
      }
    };

    return achievementTexts[achievementId]?.[userLanguage] || achievementTexts[achievementId]?.['en'] || { name: 'Achievement', description: 'You earned an achievement!' };
  };

  const loadStickerData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 StickerStats: Loading sticker data for user:', userId);

      const userStatsData = await stickerTrackingService.getUserStickerStats(userId);
      console.log('📊 StickerStats: User stats loaded:', userStatsData);

      setUserStats(userStatsData);
      setPlanStats([]); // Empty for now
    } catch (error) {
      console.error('❌ StickerStats: Error loading sticker data:', error);
      // Set default data on error
      setUserStats({
        totalStickers: 0,
        totalPlansCompleted: 0,
        totalDaysCompleted: 0,
        averageStickersPerPlan: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: new Date(),
        stickersByPlanType: {
          dynamic: 0,
          template: 0,
          communication: 0
        },
        recentStickerEvents: [],
        achievements: []
      });
      setPlanStats([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    console.log('📊 StickerStats useEffect triggered:', { open, userId });
    if (open && userId) {
      console.log('📊 Loading sticker data...');
      loadStickerData();
    }
  }, [open, userId]); // Removed loadStickerData from dependencies to prevent unnecessary re-renders

  // Refresh data when dialog is open (in case data was updated while dialog was open)
  // Note: Removed auto-refresh interval to prevent flashing
  // Data will be loaded once when dialog opens and when userId changes

  const formatDate = (date: Date | string) => {
    // Handle both Date objects and string dates
    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return isChinese ? '日期无效' :
        isSpanish ? 'Fecha inválida' :
          isFrench ? 'Date invalide' :
            'Invalid date';
    }

    // Use language-appropriate locale
    const locale = isChinese ? 'zh-CN' :
      isSpanish ? 'es' :
        isFrench ? 'fr' :
          'en-US';

    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(dateObj);
  };

  const getStreakColor = (streak: number) => {
    if (streak >= 30) return '#FFD700'; // Gold
    if (streak >= 7) return '#FF6B35'; // Orange
    if (streak >= 3) return '#4CAF50'; // Green
    return '#9E9E9E'; // Gray
  };

  const getAchievementIcon = (icon: string) => {
    return <span style={{ fontSize: '1.5rem' }}>{icon}</span>;
  };

  if (!open) {
    console.log('📊 StickerStats: Dialog is closed, returning null');
    return null;
  }

  console.log('📊 StickerStats: Dialog should be open, rendering...');

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            zIndex: 9999
          }
        }}
      >
        <DialogTitle>Sticker Statistics</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading sticker statistics...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!userStats) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            zIndex: 9999
          }
        }}
      >
        <DialogTitle>{getText('stickerStatistics')}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {getText('noDataAvailable')}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        zIndex: 9999,
        '& .MuiDialog-paper': {
          zIndex: 9999
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon color="primary" />
          <Typography variant="h5">{getText('stickerStatistics')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" title={getText('close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: isMobile ? 2 : 3, maxHeight: '80vh', overflow: 'auto' }}>

          {/* Overview Section */}
          <Accordion
            expanded={expandedSection === 'overview'}
            onChange={() => setExpandedSection(expandedSection === 'overview' ? false : 'overview')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">📊 {getText('overview')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 200, flex: '1 1 200px' }}>
                  <CardContent>
                    <Typography variant="h3" component="div">
                      {userStats.totalStickers}
                    </Typography>
                    <Typography variant="body2">
                      {getText('totalStickers')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText', minWidth: 200, flex: '1 1 200px' }}>
                  <CardContent>
                    <Typography variant="h3" component="div">
                      {userStats.totalPlansCompleted}
                    </Typography>
                    <Typography variant="body2">
                      {getText('plansCompleted')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText', minWidth: 200, flex: '1 1 200px' }}>
                  <CardContent>
                    <Typography variant="h3" component="div">
                      {userStats.totalDaysCompleted}
                    </Typography>
                    <Typography variant="body2">
                      {getText('daysCompleted')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center', bgcolor: getStreakColor(userStats.currentStreak), color: 'white', minWidth: 200, flex: '1 1 200px' }}>
                  <CardContent>
                    <Typography variant="h3" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <FireIcon />
                      {userStats.currentStreak}
                    </Typography>
                    <Typography variant="body2">
                      {getText('currentStreak')}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Stickers by Plan Type */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {getText('stickersByPlanType')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Typography variant="h4" color="primary">
                      {userStats.stickersByPlanType.dynamic}
                    </Typography>
                    <Typography variant="body2">{getText('dynamic')} Plans</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Typography variant="h4" color="secondary">
                      {userStats.stickersByPlanType.template}
                    </Typography>
                    <Typography variant="body2">{getText('template')} Plans</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Typography variant="h4" color="success">
                      {userStats.stickersByPlanType.communication}
                    </Typography>
                    <Typography variant="body2">{getText('communication')} Plans</Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Achievements Section */}
          <Accordion
            expanded={expandedSection === 'achievements'}
            onChange={() => setExpandedSection(expandedSection === 'achievements' ? false : 'achievements')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">🏆 {getText('achievements')} ({userStats.achievements.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {userStats.achievements.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                  {userStats.achievements.map((achievement, index) => (
                    <Card key={achievement.id} sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper', minWidth: 250, flex: '1 1 250px' }}>
                      <Box sx={{ mb: 1 }}>
                        {getAchievementIcon(achievement.icon)}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {getTranslatedAchievementText(achievement.id, userLanguage).name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslatedAchievementText(achievement.id, userLanguage).description}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {isChinese ? '解锁时间：' :
                          isSpanish ? 'Desbloqueado: ' :
                            isFrench ? 'Débloqué : ' :
                              'Unlocked: '}{formatDate(achievement.unlockedAt)}
                      </Typography>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isChinese ? '完成您的第一个计划来解锁成就！' :
                    isSpanish ? '¡Completa tu primer plan para desbloquear logros!' :
                      isFrench ? 'Complétez votre premier plan pour débloquer des réalisations !' :
                        'Complete your first plan to unlock achievements!'}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Plan Statistics Section */}
          <Accordion
            expanded={expandedSection === 'plans'}
            onChange={() => setExpandedSection(expandedSection === 'plans' ? false : 'plans')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">📋 {isChinese ? '计划统计' : isSpanish ? 'Estadísticas de Planes' : isFrench ? 'Statistiques de Plans' : 'Plan Statistics'} ({planStats.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {planStats.length > 0 ? (
                <List>
                  {planStats.map((plan, index) => (
                    <React.Fragment key={plan.planId}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: plan.isCompleted ? 'success.main' : 'primary.main' }}>
                            {plan.isCompleted ? <CheckCircleIcon /> : <TimelineIcon />}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="h6">
                                {plan.planTitle}
                              </Typography>
                              <Chip
                                label={plan.planType}
                                size="small"
                                color={plan.planType === 'dynamic' ? 'primary' : plan.planType === 'template' ? 'secondary' : 'success'}
                              />
                              {plan.isCompleted && (
                                <Chip label={getText('completed')} size="small" color="success" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {plan.daysCompleted} {isChinese ? '天已完成' : isSpanish ? 'días completados' : isFrench ? 'jours terminés' : 'days completed'} / {plan.totalDays} {isChinese ? '天' : isSpanish ? 'días' : isFrench ? 'jours' : 'days'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <StarIcon fontSize="small" color="primary" />
                                  <Typography variant="body2">
                                    {plan.totalStickers} {isChinese ? '贴纸' : isSpanish ? 'calcomanías' : isFrench ? 'autocollants' : 'stickers'}
                                  </Typography>
                                </Box>
                                <Box sx={{ width: 100 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={plan.completionPercentage}
                                    color={plan.isCompleted ? 'success' : 'primary'}
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {plan.completionPercentage}%
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < planStats.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isChinese ? '未找到计划。开始您的第一个计划来获得贴纸！' :
                    isSpanish ? 'No se encontraron planes. ¡Comienza tu primer plan para ganar calcomanías!' :
                      isFrench ? 'Aucun plan trouvé. Commencez votre premier plan pour gagner des autocollants !' :
                        'No plans found. Start your first plan to begin earning stickers!'}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Recent Activity Section */}
          <Accordion
            expanded={expandedSection === 'activity'}
            onChange={() => setExpandedSection(expandedSection === 'activity' ? false : 'activity')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">🕒 {getText('recentActivity')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {userStats.recentStickerEvents.length > 0 ? (
                <List>
                  {userStats.recentStickerEvents.slice(0, 10).map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: event.isBonus ? 'warning.main' : 'primary.main' }}>
                            <StarIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body1">
                                {event.description}
                              </Typography>
                              <Chip
                                label={`+${event.stickersEarned}`}
                                size="small"
                                color={event.isBonus ? 'warning' : 'primary'}
                                icon={<StarIcon />}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(event.timestamp)}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < Math.min(userStats.recentStickerEvents.length, 10) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {isChinese ? '暂无最近活动。完成一些计划活动来查看您的进度！' :
                    isSpanish ? 'No hay actividad reciente. ¡Completa algunas actividades del plan para ver tu progreso aquí!' :
                      isFrench ? 'Aucune activité récente. Complétez quelques activités de plan pour voir vos progrès ici !' :
                        'No recent activity. Complete some plan activities to see your progress here!'}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(StickerStats);
